import { FC, ReactNode, useEffect, useState } from "react";
import { MenuOutlined } from "@ant-design/icons";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { closestCenter, DndContext } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useActions } from "../../../hooks/useActions";
import getChoiceTitle from "../../../helpers/getChoiceTitle";
import { sortByOrder } from "../../../helpers/sortByOrder";
import MarkdownParser from "../../../components/markdown";

interface DraggableItemProps {
  id: string | number;
  children: (props: { listeners: any; attributes: any }) => ReactNode;
}

const DraggableItem: FC<DraggableItemProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
};

type RangingComponentProps = {
  setIsAnswerMoved: (isAnswerMoved: boolean) => void;
};

const RangingComponent: FC<RangingComponentProps> = ({ setIsAnswerMoved }) => {
  const question = useSelector((state: RootState) => state.questions.question);
  const answerChoices = question?.answerTemplate?.answerChoices ?? [];
  const mixedAnswers = question?.answerTemplate?.mixedAnswers;
  const hideLineOrder = question?.answerTemplate?.hideLineOrder ?? false;
  const { setUserAnswer } = useActions();
  const [sortedOptions, setSortedOptions] = useState<any[]>([]);

  setIsAnswerMoved(false);

  useEffect(() => {
    let initialOptions = answerChoices
      .filter((ac) => ac)
      .sort(sortByOrder)
      .map(getChoiceTitle)
      .filter((it) => !!it.choiceId)
      .map((option, index) => ({
        ...option,
        order: index + 1,
      }));

    if (mixedAnswers) {
      for (let i = initialOptions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [initialOptions[i], initialOptions[j]] = [initialOptions[j], initialOptions[i]];
      }

      initialOptions = initialOptions.map((option, index) => ({
        ...option,
        order: index + 1,
      }));
    }

    setSortedOptions(initialOptions);

    const initialChoiceObjects = initialOptions.map((option) =>
      JSON.stringify({
        value: option.choiceId,
        order: option.order,
      }),
    );

    setUserAnswer({ answerValue: "", csiAnswers: [], choices: initialChoiceObjects });
  }, [answerChoices, mixedAnswers]);

  const handleChange = () => {
    setIsAnswerMoved(true);
  };

  const handleDragEnd = (event: { active: any; over: any }) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortedOptions.findIndex((item) => item.choiceId === active.id);
      const newIndex = sortedOptions.findIndex((item) => item.choiceId === over.id);
      const newOptions = arrayMove(sortedOptions, oldIndex, newIndex);

      const reorderedOptions = newOptions.map((option, index) => ({
        ...option,
        order: index + 1,
      }));

      setSortedOptions(reorderedOptions);
      handleChange();

      const choiceObjects = reorderedOptions.map((option) =>
        JSON.stringify({
          value: option.choiceId,
          order: option.order,
        }),
      );

      setUserAnswer({ answerValue: "", csiAnswers: [], choices: choiceObjects });
    }
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortedOptions.map((item) => item.choiceId)}>
        <div className="sortable-list">
          {sortedOptions.map((option, index) => (
            <DraggableItem key={option.choiceId} id={option.choiceId}>
              {({ listeners, attributes }) => (
                <div className="item-row">
                  {!hideLineOrder && <span className="item-index">{index + 1}</span>}
                  <span
                    className={question?.surveyDecor?.textFont ? "disable-global-font" : ""}
                    style={{ fontFamily: question?.surveyDecor?.textFont }}
                  >
                    <MarkdownParser mixedString={option.label} />
                  </span>
                  <MenuOutlined
                    {...listeners}
                    {...attributes}
                    style={{
                      cursor: "grab",
                      color: "#999",
                      marginLeft: "auto",
                      touchAction: "none",
                    }}
                  />
                </div>
              )}
            </DraggableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default RangingComponent;
